import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchSellerHome,
  fetchSellerOrders,
  fetchSellerInsights,
  fetchSellerFinancial,
  fetchSellerCatalog,
  saveSellerProduct,
} from "./contentStudioApi";
import { formatShopPrice as formatPrice } from "../Shop/shopApi";
import "./ContentStudio.css";

const TABS = [
  { id: "home", label: "Overview" },
  { id: "products", label: "Products" },
  { id: "orders", label: "Orders" },
  { id: "insights", label: "Insights" },
  { id: "financial", label: "Revenue" },
];

const emptyProduct = {
  shop_product_id: "",
  name: "",
  sku: "",
  short_description: "",
  regular_price: "",
  status: "draft",
  product_type: "physical",
  inventory_mode: "unlimited",
  stock_quantity: "",
  is_free: false,
};

/**
 * Workspace commerce panel — Seller Dashboard without Admin chrome.
 */
function SellerWorkspacePanel({ sellerType, sellerId, initialTab }) {
  const [tab, setTab] = useState(initialTab || "home");
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");

  const loadTab = async (nextTab) => {
    if (!sellerType || !sellerId) {
      setError("Seller context required");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    const params = { seller_type: sellerType, seller_id: sellerId };
    if (nextTab === "products") {
      const body = await fetchSellerCatalog(params);
      if (!body || body.error || body.success === false) {
        setError((body && (body.error || body.message)) || "Unable to load products");
        setCatalog([]);
      } else {
        const payload = body.data || body;
        setCatalog((payload && payload.products) || []);
        setData(payload);
      }
    } else {
      const loaders = {
        home: fetchSellerHome,
        orders: fetchSellerOrders,
        insights: fetchSellerInsights,
        financial: fetchSellerFinancial,
      };
      const loader = loaders[nextTab] || fetchSellerHome;
      const body = await loader(params);
      if (!body || body.error || body.success === false) {
        setError((body && (body.error || body.message)) || "Unable to load storefront");
        setData(null);
      } else {
        setData(body.data || body);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerType, sellerId, tab]);

  const editProduct = (p) => {
    setForm({
      shop_product_id: p.id || "",
      name: p.name || "",
      sku: p.sku || "",
      short_description: p.short_description || "",
      regular_price: p.regular_price != null ? String(p.regular_price) : "",
      status: p.status || "draft",
      product_type: p.product_type || "physical",
      inventory_mode: p.inventory_mode || "unlimited",
      stock_quantity: p.stock_quantity != null ? String(p.stock_quantity) : "",
      is_free: !!p.is_free,
    });
    setNote("");
  };

  const onSaveProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    setNote("");
    const payload = {
      seller_type: sellerType,
      seller_id: sellerId,
      shop_product_id: form.shop_product_id || undefined,
      name: form.name,
      sku: form.sku,
      short_description: form.short_description,
      regular_price: form.regular_price,
      status: form.status,
      product_type: form.product_type,
      inventory_mode: form.inventory_mode,
      stock_quantity: form.stock_quantity,
      is_free: form.is_free ? "1" : "0",
    };
    const body = await saveSellerProduct(payload);
    setSaving(false);
    if (!body || body.success === false) {
      setNote((body && body.error) || "Unable to save product");
      return;
    }
    setNote("Product saved.");
    setForm(emptyProduct);
    loadTab("products");
  };

  return (
    <div className="cstudio seller-workspace">
      <header className="cstudio__header" style={{ marginBottom: "1rem" }}>
        <div>
          <p className="cstudio-eyebrow">Storefront</p>
          <h1>
            {(data && data.seller && (data.seller.name || data.seller.display_name)) ||
              "Seller dashboard"}
          </h1>
        </div>
      </header>

      <div className="cstudio-toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={"cstudio-btn" + (tab === t.id ? "" : " cstudio-btn--ghost")}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <p className="cstudio-muted">Loading…</p> : null}
      {error ? <p className="cstudio-error">{error}</p> : null}

      {!loading && !error && tab === "home" && data ? <HomeView data={data} /> : null}

      {!loading && !error && tab === "products" ? (
        <div className="cstudio-panel">
          <h2>Products</h2>
          <form className="cstudio-toolbar" style={{ flexWrap: "wrap" }} onSubmit={onSaveProduct}>
            <input
              required
              placeholder="Product name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <input
              placeholder="SKU"
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
            />
            <input
              placeholder="Price"
              value={form.regular_price}
              onChange={(e) => setForm({ ...form, regular_price: e.target.value })}
            />
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
            <select
              value={form.product_type}
              onChange={(e) => setForm({ ...form, product_type: e.target.value })}
            >
              <option value="physical">Physical</option>
              <option value="digital_download">Digital</option>
              <option value="streaming">Streaming</option>
              <option value="external_link">External link</option>
            </select>
            <button type="submit" className="cstudio-btn" disabled={saving}>
              {form.shop_product_id ? "Update product" : "Add product"}
            </button>
            {form.shop_product_id ? (
              <button
                type="button"
                className="cstudio-btn cstudio-btn--ghost"
                onClick={() => setForm(emptyProduct)}
              >
                Cancel edit
              </button>
            ) : null}
          </form>
          <textarea
            rows={2}
            className="cstudio-input"
            style={{ width: "100%", marginBottom: "0.75rem" }}
            placeholder="Short description"
            value={form.short_description}
            onChange={(e) =>
              setForm({ ...form, short_description: e.target.value })
            }
          />
          {note ? <p className="cstudio-muted">{note}</p> : null}
          <ul className="cstudio-list">
            {catalog.map((p) => (
              <li key={p.id}>
                <div>
                  <strong>{p.name}</strong>
                  <div className="cstudio-muted">
                    {p.status}
                    {p.regular_price != null
                      ? ` · ${formatPrice(p.regular_price) || p.regular_price}`
                      : ""}
                    {p.sku ? ` · ${p.sku}` : ""}
                  </div>
                </div>
                <div className="cstudio-toolbar" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="cstudio-btn cstudio-btn--ghost"
                    onClick={() => editProduct(p)}
                  >
                    Edit
                  </button>
                  {p.shop_path ? (
                    <Link className="cstudio-btn cstudio-btn--ghost" to={p.shop_path}>
                      View in Shop
                    </Link>
                  ) : null}
                </div>
              </li>
            ))}
            {!catalog.length ? (
              <li>
                <span className="cstudio-muted">No products yet — add one above.</span>
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {!loading && !error && tab === "orders" && data ? (
        <OrdersView data={data} />
      ) : null}
      {!loading && !error && (tab === "insights" || tab === "financial") && data ? (
        <MetricView title={tab === "insights" ? "Insights" : "Revenue"} data={data} />
      ) : null}
    </div>
  );
}

function HomeView({ data }) {
  const widgets = (data && data.widgets) || {};
  const entries = Object.keys(widgets).map((k) => ({
    key: k,
    ...(widgets[k] || {}),
  }));
  return (
    <div className="cstudio-panel">
      <h2>Overview</h2>
      <div className="cstudio-grid">
        {entries.slice(0, 12).map((w) => (
          <div className="cstudio-stat" key={w.key}>
            <strong>
              {w.title || w.label || w.key}
            </strong>
            <span className="cstudio-muted">
              {(w.items &&
                w.items
                  .slice(0, 2)
                  .map((i) => `${i.label}: ${i.value}`)
                  .join(" · ")) ||
                w.status ||
                "Ready"}
            </span>
          </div>
        ))}
      </div>
      {!entries.length ? (
        <p className="cstudio-muted">Storefront overview will appear once configured.</p>
      ) : null}
    </div>
  );
}

function OrdersView({ data }) {
  const widgets = (data && data.widgets) || {};
  const orderWidget =
    widgets.orders || widgets.recent_orders || widgets.order_summary || null;
  const items =
    (orderWidget && (orderWidget.items || orderWidget.orders)) ||
    data.orders ||
    [];
  return (
    <div className="cstudio-panel">
      <h2>Orders</h2>
      {Array.isArray(items) && items.length ? (
        <ul className="cstudio-list">
          {items.slice(0, 40).map((item, i) => (
            <li key={(item && (item.id || item.number)) || i}>
              <strong>
                {(item && (item.number || item.title || item.name || item.id)) ||
                  "Order"}
              </strong>
              <span className="cstudio-muted">
                {(item && (item.status || item.total || item.label)) || ""}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="cstudio-muted">No orders yet.</p>
      )}
    </div>
  );
}

function MetricView({ title, data }) {
  const widgets = (data && data.widgets) || {};
  const entries = Object.keys(widgets).map((k) => widgets[k]);
  return (
    <div className="cstudio-panel">
      <h2>{title}</h2>
      <div className="cstudio-grid">
        {entries.map((w, i) => (
          <div className="cstudio-stat" key={(w && w.key) || i}>
            <strong>{(w && (w.title || w.label)) || "Metric"}</strong>
            <span className="cstudio-muted">
              {(w &&
                w.items &&
                w.items
                  .slice(0, 3)
                  .map((it) => `${it.label}: ${it.value}`)
                  .join(" · ")) ||
                "—"}
            </span>
          </div>
        ))}
      </div>
      {!entries.length ? <p className="cstudio-muted">No data yet.</p> : null}
    </div>
  );
}

export default SellerWorkspacePanel;
